import { createRoot } from 'react-dom/client';
import { App } from './App';
import { ErrorBoundary } from './ErrorBoundary';
import { emitToParent, isEmbedded, waitForEmbedConfig } from './lib/embedHost';

const getRootDOMElement = () => {
  const id = 'extension-root';
  const existingRoot = document.getElementById(id);
  if (existingRoot) {
    return existingRoot;
  }

  const rootContainer = document.createElement('div');
  rootContainer.setAttribute('id', id);
  document.body.appendChild(rootContainer);

  return rootContainer;
};

let root: ReturnType<typeof createRoot> | null = null;

const renderApp = (Component: typeof App) => {
  const rootDOMElement = getRootDOMElement();
  if (!root) {
    root = createRoot(rootDOMElement);
  }

  root.render(
    <ErrorBoundary>
      <Component />
    </ErrorBoundary>,
  );
};

const boot = async (): Promise<void> => {
  // Path A: parent pre-populated `window.LOOKER_CONFIG` in the same window
  // (legacy same-window boot — kept working for any host still doing this).
  if (window.LOOKER_CONFIG) {
    renderApp(App);
    emitToParent({ type: 'studio:mounted' });
    return;
  }

  // Path B: iframe embed. Handshake with the parent via postMessage; boot
  // when `studio:init` arrives. See src/lib/embedHost.ts for the protocol.
  if (isEmbedded()) {
    await waitForEmbedConfig();
    renderApp(App);
    emitToParent({ type: 'studio:mounted' });
    return;
  }

  // Path C: not embedded and no config — nothing we can do.
  // eslint-disable-next-line no-console
  console.warn('[studio] no LOOKER_CONFIG and not embedded; refusing to boot');
};

if (document.readyState === 'interactive' || document.readyState === 'complete') {
  void boot();
} else {
  window.addEventListener('DOMContentLoaded', () => {
    void boot();
  });
}
